import { channelPostAuthorTypeSchema, channelFeedReactionSchema, channelFeedReactionSummarySchema, channelFeedEngagementSchema, } from "./feed-engagement.js";
export { channelPostAuthorTypeSchema, channelFeedReactionSchema, channelFeedReactionActorSchema, channelFeedReactionSummarySchema, channelFeedEngagementSchema, } from "./feed-engagement.js";
import { z } from "zod";
import { channelFeedArtifactSchema, channelFeedRelatedArtifactsSchema } from "./feed-artifacts.js";
export { channelFeedRelatedArtifactsSchema, } from "./feed-artifacts.js";
import { sessionChannelActivitySchema } from "./session-channel-activity.js";
import { channelCannotOpenSchema } from "./resource-references.js";
import { externalClientAppSchema } from "./client-attribution.js";
import { feedActorSchema, feedRecordDetailsSchema } from "./feed.js";
import { twinSessionSummarySchema } from "./twin-public-sessions.js";
import { channelAuthorshipProvenanceSchema } from "./provenance.js";
export const channelPostSourceSchema = z.enum(["web", "cli", "api", "mcp", "agent"]);
export const channelFeedSubjectTypeSchema = z.enum(["post", "session", "artifact", "file", "record"]);
/**
 * Threads with no channel (docs/specs/shared-review-reviewers.md, "Discussions"): a matched pair, keyed
 * `<matcherSlug>:<subjectKey>`, and an entity, keyed by the id that names it now. Who reads them follows the sources a
 * viewer can read, not channel membership, so they are reached through `/api/twin/threads`, never a channel's feed.
 * A share's thread (docs/specs/twin-shares.md), keyed by the share's id, is reached only through the share's own
 * routes and its one access check: `/api/twin/threads` keeps its own narrower list. A shared mailbox's Gmail
 * conversation (`mailbox_conversation`, section 3), keyed `<connection id>:<Gmail thread id>`, is read by whoever can
 * read that mailbox now.
 */
export const twinThreadSubjectTypeSchema = z.enum(["entity_match", "entity", "share", "mailbox_conversation"]);
/** Anything a feed thread can be about: a channel's subjects and the subjects with no channel. */
export const feedThreadSubjectTypeSchema = z.enum([...channelFeedSubjectTypeSchema.options,
    ...twinThreadSubjectTypeSchema.options]);
const channelFeedMentionedUserIdsValueSchema = z.array(z.string().trim().min(1).max(200)).max(20);
export const channelFeedMentionedUserIdsSchema = channelFeedMentionedUserIdsValueSchema.default([]);
const channelFeedMentionedViewIdsValueSchema = z.array(z.string().trim().min(1).max(200)).max(20);
export const channelFeedMentionedViewIdsSchema = channelFeedMentionedViewIdsValueSchema.default([]);
const channelFeedMentionedChannelIdsValueSchema = z.array(z.string().trim().min(1).max(200)).max(20);
export const channelFeedMentionedChannelIdsSchema = channelFeedMentionedChannelIdsValueSchema.default([]);
const channelFeedMentionedDataSetIdsValueSchema = z.array(z.string().trim().min(1).max(200)).max(20);
export const channelFeedMentionedDataSetIdsSchema = channelFeedMentionedDataSetIdsValueSchema.default([]);
/** Twin entities a post or comment mentions, by id; a reader sees names only for those its data access shows. */
const channelFeedMentionedEntityIdsValueSchema = z.array(z.string().trim().min(1).max(200)).max(10);
export const channelFeedMentionedEntityIdsSchema = channelFeedMentionedEntityIdsValueSchema.default([]);
/** A mentioned Twin entity under its current name, resolved through the reader's own data access. */
export const channelFeedMentionedEntitySchema = z.object({
    id: z.string().trim().min(1),
    name: z.string(),
}).strict();
const channelFeedMentionedEntitiesSchema = z.array(channelFeedMentionedEntitySchema).max(10).default([]);
export const channelPostSchema = z.object({
    id: z.string().trim().min(1),
    channelId: z.string().trim().min(1),
    parentPostId: z.string().trim().min(1).optional(),
    body: z.string().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
    mentionedEntities: channelFeedMentionedEntitiesSchema,
    authorId: z.string().trim().min(1),
    authorType: channelPostAuthorTypeSchema,
    source: channelPostSourceSchema,
    clientApp: externalClientAppSchema.optional(),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
    authorship: channelAuthorshipProvenanceSchema.optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
    replyCount: z.number().int().nonnegative().default(0),
});
export const createChannelPostInputSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
    source: channelPostSourceSchema.default("api"),
    clientApp: externalClientAppSchema.optional(),
}).strict();
export const updateChannelPostInputSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsValueSchema.optional(),
    mentionedChannelIds: channelFeedMentionedChannelIdsValueSchema.optional(),
    mentionedDataSetIds: channelFeedMentionedDataSetIdsValueSchema.optional(),
    mentionedViewIds: channelFeedMentionedViewIdsValueSchema.optional(),
    mentionedEntityIds: channelFeedMentionedEntityIdsValueSchema.optional(),
}).strict();
export const createChannelPostCommandInputSchema = z.object({
    channelId: z.string().trim().min(1).max(200),
    parentPostId: z.string().trim().min(1).max(200).optional(),
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
}).strict();
export const updateChannelPostCommandInputSchema = z.object({
    channelId: z.string().trim().min(1).max(200),
    postId: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsValueSchema.optional(),
    mentionedChannelIds: channelFeedMentionedChannelIdsValueSchema.optional(),
    mentionedDataSetIds: channelFeedMentionedDataSetIdsValueSchema.optional(),
    mentionedViewIds: channelFeedMentionedViewIdsValueSchema.optional(),
    mentionedEntityIds: channelFeedMentionedEntityIdsValueSchema.optional(),
}).strict();
export const deleteChannelPostCommandInputSchema = updateChannelPostCommandInputSchema
    .omit({ body: true, mentionedUserIds: true, mentionedChannelIds: true,
    mentionedDataSetIds: true, mentionedViewIds: true, mentionedEntityIds: true })
    .strict();
export const channelFeedFileSchema = z.object({
    addedToChannel: z.boolean().optional(),
    id: z.string().trim().min(1),
    channelId: z.string().trim().min(1),
    sessionId: z.string().trim().min(1).optional(),
    recordId: z.string().trim().min(1).optional(),
    fileName: z.string().trim().min(1),
    contentType: z.string().trim().min(1),
    byteSize: z.number().int().nonnegative(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
});
export const channelFeedDiscussionCommentPreviewSchema = z.object({
    id: z.string().trim().min(1),
    body: z.string().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
    mentionedEntities: channelFeedMentionedEntitiesSchema,
    authorId: z.string().trim().min(1),
    authorType: channelPostAuthorTypeSchema,
    source: channelPostSourceSchema,
    clientApp: externalClientAppSchema.optional(),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
    authorship: channelAuthorshipProvenanceSchema.optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
}).strict();
export const channelFeedItemSchema = z.discriminatedUnion("type", [
    z.object({
        id: z.string().trim().min(1),
        type: z.literal("post"),
        occurredAt: z.number(),
        post: channelPostSchema,
        engagement: channelFeedEngagementSchema,
    }),
    z.object({
        id: z.string().trim().min(1),
        type: z.literal("artifact"),
        occurredAt: z.number(),
        artifact: channelFeedArtifactSchema,
        engagement: channelFeedEngagementSchema,
    }),
    z.object({
        id: z.string().trim().min(1),
        type: z.literal("file"),
        occurredAt: z.number(),
        file: channelFeedFileSchema,
        engagement: channelFeedEngagementSchema,
    }),
    z.object({
        id: z.string().trim().min(1),
        type: z.literal("session"),
        occurredAt: z.number(),
        activity: sessionChannelActivitySchema,
        relatedArtifacts: channelFeedRelatedArtifactsSchema,
        engagement: channelFeedEngagementSchema,
    }),
    z.object({
        id: z.string().trim().min(1),
        type: z.literal("record"),
        occurredAt: z.number(),
        record: feedRecordDetailsSchema.extend({
            id: z.string().trim().min(1),
            recordId: z.string().trim().min(1),
            operation: z.enum(["created", "updated", "archived"]),
            actor: feedActorSchema,
        }).strict(),
        engagement: channelFeedEngagementSchema,
    }),
    z.object({
        id: z.string().trim().min(1),
        type: z.literal("discussion"),
        occurredAt: z.number(),
        discussion: z.object({
            threadId: z.string().trim().min(1),
            subjectType: channelFeedSubjectTypeSchema,
            subjectId: z.string().trim().min(1),
            title: z.string().trim().min(1),
            latestComment: channelFeedDiscussionCommentPreviewSchema,
        }).strict(),
        engagement: channelFeedEngagementSchema,
    }),
]);
export const twinPublicSessionChannelActivitySchema = sessionChannelActivitySchema.extend({
    session: twinSessionSummarySchema,
}).strict();
const twinPublicChannelFeedSessionItemSchema = z.object({
    id: z.string().trim().min(1),
    type: z.literal("session"),
    occurredAt: z.number(),
    activity: twinPublicSessionChannelActivitySchema,
    relatedArtifacts: channelFeedRelatedArtifactsSchema,
    engagement: channelFeedEngagementSchema,
}).strict();
export const twinPublicChannelFeedItemSchema = z.union([
    twinPublicChannelFeedSessionItemSchema,
    channelFeedItemSchema,
]);
export const twinPublicChannelFeedResponseSchema = z.object({
    items: z.array(twinPublicChannelFeedItemSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export const listChannelFeedParamsSchema = z.object({
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const listChannelFeedResponseSchema = z.object({
    items: z.array(channelFeedItemSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export const getChannelPostResponseSchema = z.object({ item: channelFeedItemSchema });
export const createChannelPostResponseSchema = z.object({
    post: channelPostSchema,
    /** Items the post links to that some of the channel's readers cannot open. */
    channelCannotOpen: channelCannotOpenSchema.optional(),
});
export const listChannelPostRepliesResponseSchema = z.object({
    replies: z.array(channelPostSchema),
});
/** On a comment that is a review decision's note (docs/specs/shared-review-reviewers.md): the card and its decision. */
export const twinReviewDecisionMarkerSchema = z.object({
    proposalId: z.string().trim().min(1).max(200),
    decision: z.enum(["accept", "decline"]),
}).strict();
export const channelFeedCommentSchema = z.object({
    id: z.string().trim().min(1),
    threadId: z.string().trim().min(1),
    body: z.string().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
    mentionedEntities: channelFeedMentionedEntitiesSchema,
    authorId: z.string().trim().min(1),
    authorType: channelPostAuthorTypeSchema,
    source: channelPostSourceSchema,
    clientApp: externalClientAppSchema.optional(),
    sourceSessionId: z.string().trim().min(1).optional(),
    sourceRunId: z.string().trim().min(1).optional(),
    authorship: channelAuthorshipProvenanceSchema.optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
    canEdit: z.boolean(),
    unread: z.boolean(),
    reactions: z.array(channelFeedReactionSummarySchema),
    /** Set when the comment is a review decision's note. */
    reviewDecision: twinReviewDecisionMarkerSchema.nullable().optional(),
    /** In a shared mailbox's conversation: the Inbox item, one message, the comment was written against. */
    subjectMessageId: z.string().trim().min(1).max(500).optional(),
}).strict();
export const channelFeedThreadSchema = z.object({
    id: z.string().trim().min(1).nullable(),
    subjectType: feedThreadSubjectTypeSchema,
    subjectId: z.string().trim().min(1),
    comments: z.array(channelFeedCommentSchema),
    engagement: channelFeedEngagementSchema,
}).strict();
export const channelFeedSubjectParamsSchema = z.object({
    subjectType: channelFeedSubjectTypeSchema,
    subjectId: z.string().trim().min(1).max(300),
}).strict();
export const createChannelFeedCommentInputSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
    source: channelPostSourceSchema.default("api"),
}).strict();
/**
 * A comment in a shared mailbox's conversation names the message it was written against: Gmail can group unrelated mail
 * into one conversation, so its view shows which (docs/specs/twin-shares.md, section 3; finding 24).
 */
export const createMailboxConversationCommentInputSchema = createChannelFeedCommentInputSchema.extend({
    subjectMessageId: z.string().trim().min(1).max(500),
}).strict();
export const updateChannelFeedCommentInputSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsValueSchema.optional(),
    mentionedChannelIds: channelFeedMentionedChannelIdsValueSchema.optional(),
    mentionedDataSetIds: channelFeedMentionedDataSetIdsValueSchema.optional(),
    mentionedViewIds: channelFeedMentionedViewIdsValueSchema.optional(),
    mentionedEntityIds: channelFeedMentionedEntityIdsValueSchema.optional(),
}).strict();
export const setChannelFeedReactionInputSchema = z.object({
    reaction: channelFeedReactionSchema,
    active: z.boolean(),
    commentId: z.string().trim().min(1).max(200).optional(),
    source: channelPostSourceSchema.default("api"),
}).strict();
export const setChannelFeedThreadReadStateInputSchema = z.object({
    unread: z.boolean(),
    commentId: z.string().trim().min(1).max(200).optional(),
}).strict();
export const channelFeedThreadReadStateSchema = z.object({
    unreadCount: z.number().int().nonnegative(),
    latestCommentId: z.string().trim().min(1).nullable(),
    readThroughCommentId: z.string().trim().min(1).nullable(),
}).strict();
export const getChannelFeedThreadCommandInputSchema = channelFeedSubjectParamsSchema.extend({
    channelId: z.string().trim().min(1).max(200),
}).strict();
export const createChannelFeedCommentCommandInputSchema = getChannelFeedThreadCommandInputSchema.extend({
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsSchema,
    mentionedChannelIds: channelFeedMentionedChannelIdsSchema,
    mentionedDataSetIds: channelFeedMentionedDataSetIdsSchema,
    mentionedViewIds: channelFeedMentionedViewIdsSchema,
    mentionedEntityIds: channelFeedMentionedEntityIdsSchema,
}).strict();
export const updateChannelFeedCommentCommandInputSchema = z.object({
    channelId: z.string().trim().min(1).max(200),
    commentId: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(10_000),
    mentionedUserIds: channelFeedMentionedUserIdsValueSchema.optional(),
    mentionedChannelIds: channelFeedMentionedChannelIdsValueSchema.optional(),
    mentionedDataSetIds: channelFeedMentionedDataSetIdsValueSchema.optional(),
    mentionedViewIds: channelFeedMentionedViewIdsValueSchema.optional(),
    mentionedEntityIds: channelFeedMentionedEntityIdsValueSchema.optional(),
}).strict();
export const deleteChannelFeedCommentCommandInputSchema = updateChannelFeedCommentCommandInputSchema
    .omit({ body: true, mentionedUserIds: true, mentionedChannelIds: true,
    mentionedDataSetIds: true, mentionedViewIds: true, mentionedEntityIds: true }).strict();
export const setChannelFeedReactionCommandInputSchema = getChannelFeedThreadCommandInputSchema.extend({
    reaction: channelFeedReactionSchema,
    active: z.boolean(),
    commentId: z.string().trim().min(1).max(200).optional(),
}).strict();
export const channelFeedThreadResponseSchema = z.object({ thread: channelFeedThreadSchema }).strict();
export const channelFeedCommentResponseSchema = z.object({ comment: channelFeedCommentSchema }).strict();
export const channelFeedEngagementResponseSchema = z.object({
    engagement: channelFeedEngagementSchema,
    comment: channelFeedCommentSchema.optional(),
}).strict();
export const channelFeedThreadReadStateResponseSchema = z.object({
    readState: channelFeedThreadReadStateSchema,
}).strict();
